import { FetchResult, useQuery } from "@apollo/client";
import { Button, styled } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  ListSubaccountEscalationInboxesForTexterQuery,
  ListSubaccountEscalationInboxesForTexterQueryVariables,
  PhoneNumberMessageClass,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LIST_SUBACCOUNT_ESCALATION_INBOXES_FOR_TEXTER } from "~frontend/graphql/inboxes";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";

import { EscalateDialog } from "./EscalateDialog";
import { MoveEscalationDialog } from "./MoveEscalationDialog";
import OptInDialog from "./OptInDialog";
import { Thread } from "./types";

const ThreadButton = styled(Button)({
  color: palette.navy,
  fontSize: "10px",
  fontWeight: 500,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  padding: "6px 8px",
  height: "32px",
});

export default function Buttons({
  optInThread,
  isOptedOut,
  handleOptOut,
  handleOptIn,
  needsResponse,
  handleMarkNeedsResponse,
  handleMarkNotNeedsResponse,
  handleEscalate,
  handleDeescalate,
  mutationPending,
  ownedPhoneNumber,
  inboxName,
}: {
  isOptedOut: boolean;
  handleOptOut: () => Promise<unknown>;
  handleOptIn?: null | (() => Promise<unknown>);
  optInThread?: Thread;
  needsResponse: boolean;
  handleMarkNeedsResponse: null | (() => Promise<unknown>);
  handleMarkNotNeedsResponse: null | (() => Promise<unknown>);
  handleEscalate: (escalationInboxId: string) => Promise<FetchResult<unknown>>;
  handleDeescalate: null | (() => Promise<unknown>);
  mutationPending: boolean;
  ownedPhoneNumber: Thread["fromOwnedPhoneNumber"];
  inboxName: string | null;
}): ReactElement {
  const confirm = useConfirm();
  const intl = useIntl();
  const [optInDialogOpen, setOptInDialogOpen] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [moveEscalationDialogOpen, setMoveEscalationDialogOpen] =
    useState(false);

  const { subaccount } = useSubaccount()!;
  const listEscalationInboxes = useQuery<
    ListSubaccountEscalationInboxesForTexterQuery,
    ListSubaccountEscalationInboxesForTexterQueryVariables
  >(LIST_SUBACCOUNT_ESCALATION_INBOXES_FOR_TEXTER, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const escalateOptions =
    listEscalationInboxes.data?.listSubaccountEscalationInboxes.map(
      (inbox: { id: string; name?: string | null | undefined }) => {
        return {
          // Escalation inboxes always have a name
          inboxName: inbox.name!,
          inboxId: inbox.id,
        };
      }
    );

  const isShortCode =
    ownedPhoneNumber.messageClass === PhoneNumberMessageClass.ShortCode;

  const shortCodeOptInDialog = async () => {
    const { confirmed } = await confirm({
      title: intl.formatMessage({
        defaultMessage: "Are you sure you want to resubscribe this contact?",
        description: "Title for resubscribe modal in Thread view",
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'Are you sure you want to resubscribe this contact? They will be sent your short code\'s welcome message: "{shortCodeWelcomeMessage}"',
          description: "Body text for resubscribe modal in Thread view",
        },
        {
          shortCodeWelcomeMessage:
            ownedPhoneNumber.shortCodeConfig!.optInMessage,
        }
      ),
      cancellationText: intl.formatMessage({
        defaultMessage: "Cancel",
        description: "Cancel button",
      }),
      confirmationText: intl.formatMessage({
        defaultMessage: "Confirm resubscribe",
        description:
          "Button to confirm resubscribing a contact from the Thread view",
      }),
    });
    if (!confirmed) {
      return;
    }

    await handleOptIn!();
  };

  const optOutDialog = async () => {
    if (ownedPhoneNumber.messageClass === PhoneNumberMessageClass.ShortCode) {
      const { confirmed } = await confirm({
        title: intl.formatMessage({
          defaultMessage: "Unsubscribe Contact?",
          description: "Title for unsubscribe modal in Thread view",
        }),
        description: intl.formatMessage(
          {
            defaultMessage:
              'Are you sure you want to unsubscribe this contact? They will be sent your short code\'s opt out message: "{shortCodeGoodbyeMessage}"',
            description: "Body text for unsubscribe modal in Thread view",
          },
          {
            shortCodeGoodbyeMessage:
              ownedPhoneNumber.shortCodeConfig!.optOutMessage,
          }
        ),
        cancellationText: intl.formatMessage({
          defaultMessage: "Cancel",
          description: "Cancel button",
        }),
        confirmationText: intl.formatMessage({
          defaultMessage: "Confirm Unsubscribe",
          description:
            "Button to confirm unsubscribing a contact from the Thread view",
        }),
      });
      if (!confirmed) {
        return;
      }
    } else {
      const { confirmed } = await confirm({
        title: intl.formatMessage({
          defaultMessage: "Opt Out Contact?",
          description: "Title for Opt Out modal in Thread view",
        }),
        description: intl.formatMessage({
          defaultMessage: "Are you sure you want to Opt Out this contact?",
          description: "Body text for Opt Out modal in Thread view",
        }),
        cancellationText: intl.formatMessage({
          defaultMessage: "Cancel",
          description: "Cancel button",
        }),
        confirmationText: intl.formatMessage({
          defaultMessage: "Confirm Opt Out",
          description:
            "Button to confirm opting out a contact from the Thread view",
        }),
      });
      if (!confirmed) {
        return;
      }
    }
    await handleOptOut();
  };

  const deescalateDialog = async () => {
    const { confirmed } = await confirm({
      title: intl.formatMessage(
        {
          defaultMessage:
            "Release this conversation from the {inboxName} escalation inbox?",
          description: "dialog title for confirming deescalation",
        },
        {
          inboxName,
        }
      ),
      description: intl.formatMessage({
        defaultMessage:
          "Releasing this message will move it back to its original campaign inbox, where you may no longer have access to it.",
        description:
          "Warning that releasing a message moves it back to campaign inbox",
      }),
      cancellationText: intl.formatMessage({
        defaultMessage: "Cancel",
        description: "Cancel button",
      }),
      confirmationText: intl.formatMessage({
        defaultMessage: "De-escalate",
        description: "Button to confirm deescalating a thread",
      }),
    });
    if (!confirmed) {
      return;
    }

    await handleDeescalate!();
  };

  const hasEscalationOptions = escalateOptions && escalateOptions.length > 0;

  const showMoveEscalationButton =
    handleDeescalate && escalateOptions && escalateOptions.length > 1;

  return (
    <FlexBox
      flexDirection="row"
      justifyContent="space-between"
      sx={{ marginTop: "10px" }}
    >
      {isOptedOut ? (
        handleOptIn ? (
          <ThreadButton
            variant="contained"
            color="secondary"
            sx={{ color: palette.white }}
            disabled={mutationPending}
            onClick={
              isShortCode
                ? shortCodeOptInDialog
                : () => setOptInDialogOpen(true)
            }
          >
            {isShortCode ? (
              <FormattedMessage
                defaultMessage="Subscribe"
                description="Button text to subscribe a contact"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Opt In"
                description="Button text to Opt In a contact"
              />
            )}
          </ThreadButton>
        ) : (
          <div />
        )
      ) : (
        <ThreadButton
          variant="outlined"
          color="error"
          disabled={mutationPending}
          onClick={optOutDialog}
          sx={{ color: palette.red }}
        >
          {isShortCode ? (
            <FormattedMessage
              defaultMessage="Unsubscribe"
              description="Button text to unsubscribe a contact"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Opt Out"
              description="Button text to Opt Out a contact"
            />
          )}
        </ThreadButton>
      )}

      <FlexBox flexDirection="row" sx={{ gap: theme.spacing(1) }}>
        {handleMarkNeedsResponse && handleMarkNotNeedsResponse ? (
          needsResponse ? (
            <ThreadButton
              variant="outlined"
              color="secondary"
              disabled={mutationPending}
              onClick={handleMarkNotNeedsResponse}
            >
              <FormattedMessage
                defaultMessage="Mark as not needing response"
                description="Button text to mark a contact as not needing a response"
              />
            </ThreadButton>
          ) : (
            <ThreadButton
              variant="contained"
              color="secondary"
              sx={{
                color: palette.white,
              }}
              disabled={mutationPending}
              onClick={handleMarkNeedsResponse}
            >
              <FormattedMessage
                defaultMessage="Mark as needing response"
                description="Button text to mark a contact a needing a response"
              />
            </ThreadButton>
          )
        ) : null}
        {handleDeescalate ? (
          <>
            {showMoveEscalationButton && (
              <ThreadButton
                variant="contained"
                color="secondary"
                sx={{
                  color: palette.white,
                }}
                disabled={mutationPending}
                onClick={() => {
                  setMoveEscalationDialogOpen(true);
                }}
              >
                <FormattedMessage
                  defaultMessage="Move Escalation"
                  description="Button text to move an escalation to another inbox"
                />
              </ThreadButton>
            )}
            <ThreadButton
              variant="contained"
              color="secondary"
              sx={{
                color: palette.white,
              }}
              disabled={mutationPending}
              onClick={deescalateDialog}
            >
              <FormattedMessage
                defaultMessage="De-escalate"
                description="Button text to deescalate a thread"
              />
            </ThreadButton>
          </>
        ) : hasEscalationOptions ? (
          <ThreadButton
            variant="outlined"
            color="secondary"
            disabled={mutationPending}
            onClick={() => {
              setEscalateDialogOpen(true);
            }}
          >
            <FormattedMessage
              defaultMessage="Escalate"
              description="Button text to escalate a thread"
            />
          </ThreadButton>
        ) : null}
      </FlexBox>
      {optInThread && handleOptIn ? (
        <OptInDialog
          phoneNumber={optInThread.toPhoneNumber}
          deleteOptOut={handleOptIn}
          open={optInDialogOpen && !isShortCode}
          onClose={() => setOptInDialogOpen(false)}
        />
      ) : null}
      {hasEscalationOptions ? (
        handleDeescalate ? (
          <MoveEscalationDialog
            isOpen={moveEscalationDialogOpen}
            inboxName={inboxName!}
            escalateOptions={escalateOptions}
            handleEscalate={handleEscalate}
            handleClose={() => {
              setMoveEscalationDialogOpen(false);
            }}
          />
        ) : (
          <EscalateDialog
            isOpen={escalateDialogOpen}
            escalateOptions={escalateOptions}
            handleEscalate={handleEscalate}
            handleClose={() => {
              setEscalateDialogOpen(false);
            }}
          />
        )
      ) : null}
    </FlexBox>
  );
}
